import { api } from '@/lib/axios';

export interface Department {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    conversations: number;
  };
  users?: DepartmentUser[];
}

export interface DepartmentUser {
  userId: string;
  departmentId: string;
  isManager: boolean;
  joinedAt: string;
  user: {
    id: string;
    name: string;
    email: string;
  };
}

export interface CreateDepartmentDto {
  name: string;
  description?: string;
}

export interface UpdateDepartmentDto {
  name?: string;
  description?: string;
}

export interface AddUserToDepartmentDto {
  userId: string;
  isManager?: boolean;
}

export const departmentsService = {
  async getAll(): Promise<Department[]> {
    const response = await api.get<Department[]>('/departments');
    return response.data;
  },

  async getMyDepartments(): Promise<Department[]> {
    const response = await api.get<Department[]>('/departments/my');
    return response.data;
  },

  async getById(id: string): Promise<Department> {
    const response = await api.get<Department>(`/departments/${id}`);
    return response.data;
  },

  async create(data: CreateDepartmentDto): Promise<Department> {
    const response = await api.post<Department>('/departments', data);
    return response.data;
  },

  async update(id: string, data: UpdateDepartmentDto): Promise<Department> {
    const response = await api.patch<Department>(`/departments/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/departments/${id}`);
  },

  async setAsDefault(id: string): Promise<Department> {
    const response = await api.post<Department>(`/departments/default/${id}`);
    return response.data;
  },

  async getUsers(id: string): Promise<DepartmentUser[]> {
    const response = await api.get<DepartmentUser[]>(`/departments/${id}/users`);
    return response.data;
  },

  async addUser(departmentId: string, data: AddUserToDepartmentDto): Promise<DepartmentUser> {
    const response = await api.post<DepartmentUser>(`/departments/${departmentId}/users`, data);
    return response.data;
  },

  async removeUser(departmentId: string, userId: string): Promise<void> {
    await api.delete(`/departments/${departmentId}/users/${userId}`);
  },

  async updateUserRole(departmentId: string, userId: string, isManager: boolean): Promise<DepartmentUser> {
    const response = await api.patch<DepartmentUser>(
      `/departments/${departmentId}/users/${userId}`,
      { isManager }
    );
    return response.data;
  },
};
